'use client'

import React from 'react'

import type { ImageBlockBlock } from '@/payload-types'
import { Media } from '@/components/Media'
import { cn } from '@/utilities/ui'
import { getBlockPadding } from '@/utilities/getBlockPadding'

type Props = ImageBlockBlock & {
  disableInnerContainer?: boolean
}

const backgroundColorMap: Record<string, string> = {
  white: 'bg-civo-white',
  black: 'bg-civo-black',
  green: 'bg-civo-lime',
  cloud: 'bg-civo-cloud',
}

// Width calculations for each images-per-row option, accounting for 24px gaps (gap-6)
const widthMap: Record<string, string> = {
  '2': 'lg:w-[calc((100%-24px)/2)]',
  '3': 'lg:w-[calc((100%-48px)/3)]',
  '4': 'lg:w-[calc((100%-72px)/4)]',
  '5': 'lg:w-[calc((100%-96px)/5)]',
  '6': 'lg:w-[calc((100%-120px)/6)]',
}

export const ImageBlock: React.FC<Props> = ({ images = [], imagesPerRow = '4', backgroundColor = 'white', disableInnerContainer, paddingTop, paddingBottom }) => {
  // Get padding classes based on layout options
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? true,
  })
  if (!images || images.length === 0) {
    return null
  }

  const bgColorClass = backgroundColorMap[backgroundColor || 'white'] || backgroundColorMap.white

  return (
    <div
      className={cn('bg-civo-white', paddingClasses, {
        container: !disableInnerContainer,
      })}
    >
      <div className="flex flex-col items-start justify-start w-full">
        {/* Image Grid - Mobile First Responsive */}
        <div className="w-full">
          <div className="grid grid-cols-2 gap-2 items-center justify-center md:grid-cols-3 lg:flex lg:flex-row lg:flex-wrap lg:gap-6 lg:items-end lg:justify-center">
            {images.map((image, index) => {
              if (typeof image === 'object' && image) {
                return (
                  <div
                    key={index}
                    className={cn(
                      "w-full h-28 box-border flex items-center justify-center p-6 rounded-lg md:rounded-[8px] lg:h-40 lg:rounded-[16px] lg:px-12 lg:py-20 transition-transform duration-300 ease-in-out hover:scale-105",
                      bgColorClass,
                      widthMap[imagesPerRow || '4'] || widthMap['4'],
                    )}
                  >
                    <Media
                      resource={image}
                      className="max-h-32 w-auto object-contain"
                      imgClassName="max-h-32 w-auto object-contain"
                    />
                  </div>
                )
              }
              return null
            })}
          </div>
        </div>
      </div>
    </div>
  )
}